import styles from './import.module.scss';
import { useContext, useState } from 'react';
import { useMakeRequestHook, useTableConfigHook } from '../../../../requestUtils';
import { booleanFormatter, formatter } from '../../../../helpers';
import { ValueFormatterParams } from 'ag-grid-community';
import { booleanHeader, Button, CreateHeaders, HeaderSizeEnum, Icon, PrimaryButton, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import { IVerifiedItemInfo, IVerifiedItemValue } from '../../../../_models/verified-item-info';
import { VerifyStatusEnum } from '../../../../_enums/verify-status';
import { WebkuaTable } from '@frontend-workspace/webkua-lib';
import { useTranslation } from 'react-i18next';


/* eslint-disable-next-line */
export interface ImportProps { }

export function Import(props: ImportProps) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  const { t } = useTranslation();
  const [importSheet, setImportSheet] = useState<undefined | any>(undefined);
  const tableConfig = useTableConfigHook("ItemInfoImport");
  const [verifiedItems, setVerifiedItems] = useState<IVerifiedItemInfo[]>([]);
  const [showItemsWithoutChanges, setShowItemsWithoutChanges] = useState<boolean>(false);

  const onVerifyImport = (event: any) => {
    setImportSheet(event.target.files[0]);
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = () => {
      const excelFileBase64 = reader.result as string;
      makeRequest(`contractItemInfos/action/getFromExcel`, "POST", excelFileBase64).then(result => {
        setVerifiedItems(result);
      });
    };
  };

  const handleShowItemsWithoutChanges = (checked: boolean) => {
    setShowItemsWithoutChanges(checked)
  };

  const updateRenderer = (params: ValueFormatterParams) => {
    const handleUpdate = () => {
      makeRequest(`ContractItemInfos/action/updateFromExcel`, 'PUT', params.data).then(result => {
        setSnackbarMessage({ content: t("import.itemHasBeenUpdated"), type: "success" })
      }).catch(() => setSnackbarMessage({ content: t("import.couldNotUpdateItem"), type: "error" }));
    };
    return (
      <PrimaryButton
        variant='outlined'
        size='small'
        onClick={handleUpdate}
        disabled={!params.data.isValidForUpdate}>
        {t("import.update")}
      </PrimaryButton>
    );
  };

  
  const removeUnchangedFilter = (VerifiedItemInfo: IVerifiedItemInfo) => {
    function isVerifiedItemInfo(item: any): item is IVerifiedItemValue {
      return item && (item as IVerifiedItemValue).oldValue !== undefined;
    }
    const hasChangedValue = Object.values(VerifiedItemInfo).filter(isVerifiedItemInfo).some((item: IVerifiedItemValue) => item.status !== VerifyStatusEnum.EXISTING);
    return hasChangedValue;
  };

  const visibleItems = showItemsWithoutChanges ? verifiedItems : verifiedItems.filter(removeUnchangedFilter);

  const headers = CreateHeaders([
    textHeader("id", "ID", HeaderSizeEnum.MEDIUM, { renderer: formatter }),
    booleanHeader("isNew", t("import.newItem")),
    booleanHeader("isValidForUpdate", t("import.canBeUpdated")),
    textHeader("price", t("standard.price"), HeaderSizeEnum.LARGE, {
      renderer: formatter,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("costPrice", t("import.costPrice"), HeaderSizeEnum.LARGE, {
      renderer: formatter,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("nimbusProductCode", t("standard.nimbusCode"), HeaderSizeEnum.LARGE, { renderer: formatter }),
    textHeader("productName", t("standard.name"), HeaderSizeEnum.EXTRA_LARGE, { renderer: formatter }),
    textHeader("itemType", t("import.itemType"), HeaderSizeEnum.MEDIUM, { renderer: formatter }),
    textHeader("invoiceInterval", t("import.interval"), HeaderSizeEnum.SMALL, {
      renderer: formatter,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    booleanHeader("isNimbusLicense", t("standard.cloudLicensing"), { renderer: booleanFormatter }),
    booleanHeader("isSchoolVersion", t("standard.academicVersion"), { renderer: booleanFormatter }),
    booleanHeader("canSellNew", t("standard.canSell"), { renderer: booleanFormatter }),
    textHeader("seatsPerQuantity", t("standard.seatsPerQuantity"), HeaderSizeEnum.LARGE, {
      renderer: formatter,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("connectedItemId", t("standard.additionalProduct"), HeaderSizeEnum.LARGE, { renderer: formatter }),
    textHeader("requiredQuantity", t("import.requiredQuantity"), HeaderSizeEnum.LARGE, { renderer: formatter }),
    textHeader("currency", t("standard.currency"), HeaderSizeEnum.MEDIUM, { renderer: formatter }),
    booleanHeader("isSupportIncluded", t("import.userSupportIncluded"), { renderer: booleanFormatter }),
    textHeader("", "", HeaderSizeEnum.LARGE, {
      renderer: updateRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ])

  return <>
    <div className="d-flex flex-row align-items-start flex-wrap">
      <div className='mb-3'>
        <label htmlFor="contained-button-file">
          <input
            accept=".xlsx, .xls"
            id="contained-button-file"
            multiple type="file"
            onChange={onVerifyImport}
            style={{ display: 'none' }}
          />
          <Button
            variant="contained"
            component="span">
            <Icon className='me-1' fontSize="small">add</Icon>{t("standard.uploadExcelFile")}
          </Button>
        </label>
      </div>
    </div>
    <WebkuaTable
      headers={headers}
      tableFilters={[{
        label: t("import.showItemsWithoutChanges"),
        checked: showItemsWithoutChanges,
        onChange: handleShowItemsWithoutChanges
      }]}
      data={visibleItems}
      tableConfig={tableConfig}
    />
  </>;
}

export default Import;